import { Resources } from 'constants/resources';

import React, { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useYupSchema } from 'pages/Merchants/MerchantsShow/MerchantsShowLimits/hooks/useYupSchema';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { Form, useCreate, useNotify } from 'react-admin';
import { FieldValues } from 'react-hook-form';
import { Button } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { CardHeader } from 'shared/mui/Card/CardHeader/CardHeader';
import { Dialog } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';

import { CardContentStyled, CreateStyled } from './MerchantsShowLimitsCreateDialog.styled';
import { MerchantsShowLimitsCreateDialogForm } from './MerchantsShowLimitsCreateDialogForm/MerchantsShowLimitsCreateDialogForm';

type Props = {
  open: boolean;
  onClose: () => void;
  merchantId: string | undefined;
};

interface FormValues extends FieldValues {
  currency: string;
  gateway: string;
  paymentMax: number;
  paymentMin: number;
  payoutMax: number;
  payoutMin: number;
}

const transform = (data: FormValues) => {
  return {
    ...data,
    paymentMax: data.paymentMax ? data.paymentMax + '' : null,
    paymentMin: data.paymentMin ? data.paymentMin + '' : null,
    payoutMax: data.payoutMax ? data.payoutMax + '' : null,
    payoutMin: data.payoutMin ? data.payoutMin + '' : null,
  };
};

export const MerchantsShowLimitsCreateDialog: React.FC<Props> = ({ open, onClose, merchantId }) => {
  const translate = useTypedTranslate();
  const yupSchema = useYupSchema();

  const [create] = useCreate();
  const notify = useNotify();
  const [errors, setErrors] = useState<string[]>([]);

  const onSubmit = (formValues: FormValues) => {
    create(
      Resources.Limits,
      {
        data: {
          ...transform(formValues),
          merchant: merchantId,
        },
      },
      {
        onSuccess: () => {
          notify(translate('app.limits.limitCreatedSuccess'), {
            type: 'success',
          });
          onClose();
        },
        onError: (error) => {
          setErrors((error as Error)?.message.split('\n'));
          notify(translate('app.limits.formInvalid'), { type: 'error' });
        },
      },
    );
  };

  return (
    <Dialog onClose={onClose} open={open} scroll="body" width="640px">
      <CreateStyled resource="merchant_credentials">
        <Form onSubmit={onSubmit as any} resolver={yupResolver<FieldValues>(yupSchema)}>
          <CardHeader
            appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
            title={translate('app.limits.createLimit')}
            titleSize="smallBold"
          />
          <CardContentStyled>
            <MerchantsShowLimitsCreateDialogForm errors={errors} merchantId={merchantId} />
          </CardContentStyled>
          <CardActionsButtons
            rightActionsSlot={[
              <Button
                key="cancel"
                label={translate('app.buttons.cancel')}
                onClick={onClose}
                variant="outlined"
              />,
              <Button
                key="create"
                label={translate('app.buttons.create')}
                type="submit"
                variant="contained"
              />,
            ]}
          />
        </Form>
      </CreateStyled>
    </Dialog>
  );
};
